"use client";

import React, { useEffect, useState } from "react";
import { Clock, ArrowUpCircle, Loader2 } from "lucide-react";
import accessURL from "../../components/utils/superAccess";

interface PricingPlan {
  duration: string;
  amount: string;
}

interface Trial {
  id: number;
  duration: string;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  code: number;
  data: T;
}

const Cards: React.FC = () => {
  const [pricing, setPricing] = useState<PricingPlan[]>([]);
  const [trials, setTrials] = useState<Trial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricingRes, trialsRes] = await Promise.all([
          accessURL.get<ApiResponse<PricingPlan[]>>("getPricing"),
          accessURL.get<ApiResponse<Trial[]>>("getTrials"),
        ]);

        if (pricingRes.data.code === 200) {
          setPricing(pricingRes.data.data);
        }
        if (trialsRes.data.code === 200) {
          setTrials(trialsRes.data.data);
        }
      } catch (err) {
        setError("Failed to fetch data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDuration = (duration: string): string => {
    return duration.replace(/(\d+)(\w+)/, "$1 $2");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-red-500 dark:text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 bg-background">
      {/* Pricing Section */}
      <div className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">Pricing Plans</h2>
        <div className="space-y-4">
          {pricing.map((plan, index) => (
            <div
              key={index}
              className="group relative rounded-xl border bg-card text-card-foreground shadow transition-all hover:shadow-lg"
            >
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="relative p-6">
                <div className="flex items-center gap-4">
                  <div className="rounded-full bg-primary/10 p-2">
                    <ArrowUpCircle className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">
                      {formatDuration(plan.duration)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Premium Access Plan
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-3xl font-bold">
                    USD{parseFloat(plan.amount).toFixed(2)}
                  </span>
                  <span className="text-muted-foreground">
                    /{plan.duration.toLowerCase()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trials Section */}
      <div className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">Trial</h2>
        <div className="space-y-4">
          {trials.map((trial) => (
            <div
              key={trial.id}
              className="group relative rounded-xl border bg-card text-card-foreground shadow transition-all hover:shadow-lg"
            >
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-secondary/10 to-secondary/5 opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="relative p-6">
                <div className="flex items-center gap-4">
                  <div className="rounded-full bg-secondary/10 p-2">
                    <Clock className="h-6 w-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">
                      {formatDuration(trial.duration)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Trial Period
                    </p>
                  </div>
                </div>
                <div className="mt-4 text-sm text-muted-foreground">
                  Start your free trial today
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Cards;
